import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Col, Row, Typography, Input, Button, Alert } from "antd";
import { OverviewWrapper } from "./CustomerOverview.style";
import {
  SearchOutlined,
  FileTextOutlined,
  TeamOutlined,
  UserAddOutlined,
  RightOutlined,
  FolderOpenOutlined,
  VideoCameraOutlined,
  PhoneOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Title, Text, Paragraph } = Typography;

const CustomerOverview = () => {
  const navigate = useNavigate();
  const [profileCompletion, setProfileCompletion] = useState({ isComplete: true, completionPercentage: 100 });
  const [metrics, setMetrics] = useState({ liveJobs: 0, interviews: 0, applications: 0, hired: 0 });
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearch = () => {
    const query = searchQuery.trim();
    navigate(query ? `/customer/find-talents?role=${encodeURIComponent(query)}` : "/customer/find-talents");
  };

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const headers = { Authorization: `Bearer ${token}` };

    const checkProfileCompletion = async () => {
      try {
        const response = await axios.get(API_CONST.CHECK_PROFILE_COMPLETION, { headers });
        if (response.data) setProfileCompletion(response.data);
      } catch (error) {
        console.error("Error checking profile completion:", error);
      }
    };

    const fetchMetrics = async () => {
      try {
        const [jobsRes, hiringRes] = await Promise.all([
          axios.post(API_CONST.GET_JOB_REQUIREMENTS, { pageSize: 1000 }, { headers }),
          axios.post(API_CONST.GET_HIRING_RECORDS, { limit: 1000 }, { headers }),
        ]);

        const allJobs = jobsRes.data?.Response || jobsRes.data?.jobs || [];
        const hiring = hiringRes.data?.hiringRecords || [];
        const openJobs = allJobs.filter((j) => j.status === "open");

        const appResponses = await Promise.all(
          openJobs.map((job) =>
            axios
              .post(API_CONST.GET_JOB_APPLICATIONS, { jobId: job.id }, { headers })
              .then((r) => r.data?.applications || [])
              .catch(() => [])
          )
        );
        const applications = appResponses.flat();

        const interviewStatuses = ["interview_scheduled", "interview_initiated", "interviewing", "shortlisted"];
        const appliedStatuses = ["submitted"];

        setMetrics({
          liveJobs: openJobs.length,
          interviews: applications.filter((a) => interviewStatuses.includes(a.status)).length,
          applications: applications.filter((a) => appliedStatuses.includes(a.status)).length,
          hired: hiring.filter((h) => h.status === "active").length,
        });
      } catch (error) {
        console.error("Error fetching dashboard metrics:", error);
      }
    };

    checkProfileCompletion();
    fetchMetrics();
  }, []);

  const metricsData = [
    { count: metrics.liveJobs, label: "Job Live", icon: <FolderOpenOutlined /> },
    { count: metrics.interviews, label: "Interview Initiated", icon: <VideoCameraOutlined /> },
    { count: metrics.applications, label: "Application received", icon: <FileTextOutlined /> },
    { count: metrics.hired, label: "Talents hired", icon: <TeamOutlined /> },
  ];

  const quickActions = [
    {
      icon: <FileTextOutlined />,
      title: "Post jobs for free",
      description: "Post job briefs for your required roles and start receiving recommends & applications.",
      action: "Post a Job",
      onClick: () => navigate("/customer/post-job"),
    },
    {
      icon: <UserAddOutlined />,
      title: "Hire your first Talent",
      description: "Let's online your project — and start building your development team around its objectives.",
      action: "Initiate a Contract",
      onClick: () => navigate("/customer/find-talents"),
    },
    {
      icon: <PhoneOutlined />,
      title: "Free Consultation to hire resources",
      description: "It is a long established fact that a reader will be distracted by the readable content of a page when looking at its.",
      action: "Book a Meeting for FREE",
    },
  ];

  return (
    <OverviewWrapper>
      <Row align="middle" justify="space-between" style={{ marginBottom: "20px" }}>
        <Col>
          <Title level={2} className="page-title">Dashboard</Title>
        </Col>
      </Row>

      {!profileCompletion.isComplete && (
        <Alert
          message={
            <span>
              {profileCompletion.completionPercentage}% Your account Completed. Please complete your full account setup.{" "}
              <Button type="link" style={{ padding: 0 }} onClick={() => navigate("/customer/company-details")}>
                Complete Company Profile
              </Button>
            </span>
          }
          type="warning"
          showIcon
          closable
          style={{ marginBottom: "20px" }}
          className="account-alert"
        />
      )}

      <Row gutter={16} style={{ marginBottom: "30px" }}>
        {metricsData.map((item, index) => (
          <Col key={index} xs={12} sm={12} md={6} lg={6}>
            <Card className="metric-card">
              <div className="metric-content">
                <div className="metric-icon">{item.icon}</div>
                <div>
                  <Title level={2} className="metric-count">{item.count}</Title>
                  <Text className="metric-label">{item.label}</Text>
                </div>
                <RightOutlined className="arrow-icon" />
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <div className="hero-section">
        <Title level={3} className="hero-title">
          Explore world-class talents and pre-built tech teams from{" "}
          <span className="highlight">77800+ vetted talents</span>
        </Title>
        <Input
          size="large"
          placeholder="Search by skills or role e.g. Java, AWS, UI Designers etc."
          prefix={<SearchOutlined />}
          className="hero-search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onPressEnter={handleSearch}
          suffix={
            <Button
              type="primary"
              onClick={handleSearch}
              style={{ background: "#00d9a9", borderColor: "#00d9a9", color: "#fff" }}
            >
              Search
            </Button>
          }
        />
      </div>

      <div className="quick-action-section">
        <Title level={4} className="section-title">Quick action</Title>
        <Row gutter={24}>
          {quickActions.map((action, index) => (
            <Col key={index} xs={24} sm={24} md={8}>
              <Card className="action-card">
                <div className="action-icon-wrapper">
                  {action.icon}
                </div>
                <Title level={5} className="action-title">{action.title}</Title>
                <Paragraph className="action-description">{action.description}</Paragraph>
                <Button 
                  type="link" 
                  className="action-link"
                  onClick={action.onClick}
                >
                  {action.action} <ArrowRightOutlined />
                </Button>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      <div className="about-section">
        <Title level={4} className="section-title">About HireNXT</Title>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Card className="about-card">
              <Title level={5}>Want us to help you with Talent Need?</Title>
              <Paragraph>
                Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem ipsum has 
                been the industry's standard dummy text ever since the 1500s, when an unknown printer took 
                a galley of type and scrambled it to make a type specimen book.
              </Paragraph>
              <Paragraph className="tour-text">
                Take a tour of HireNXT by looking at walkthrough of entire product.
              </Paragraph>
              <Button type="link" className="about-link">
                Book a Consultation for FREE <ArrowRightOutlined />
              </Button>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card className="about-card">
              <Title level={5}>Schedule a Product tour</Title>
              <Paragraph>
                Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem ipsum has 
                been the industry's standard dummy text ever since the 1500s, when an unknown printer took 
                a galley of type and scrambled it to make a type specimen book.
              </Paragraph>
              <Paragraph className="tour-text">
                Take a tour of HireNXT by looking at walkthrough of entire product.
              </Paragraph>
              <Button type="link" className="about-link">
                Book a Product tour <ArrowRightOutlined />
              </Button>
            </Card>
          </Col>
        </Row>
      </div>
    </OverviewWrapper>
  );
};

export default CustomerOverview;
